import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Database } from "lucide-react";
import PublicPageLayout from "@/components/PublicPageLayout";
import Seo, { pageSchema } from "@/components/Seo";
import { WhatsAppButton } from "@/components/WhatsAppFab";
import { Button } from "@/components/ui/button";
import { WA_MESSAGES, PRICING, COVERAGE_FACTS } from "@/lib/site";

const DATA_FIELDS = [
  "CIN / LLPIN", "Company / LLP Name", "Date of Incorporation", "ROC Jurisdiction",
  "Registered State", "Registered District", "Company Category & Class",
  "Company Sub-Category", "Authorised Capital", "Paid-up Capital",
  "Director / Designated Partner Names", "Director DINs",
  "Registered Address", "Email (where available)", "Activity / Industry Code",
];

const USE_CASES = [
  { t: "B2B Sales & Lead Generation", d: "Reach founders and directors of freshly incorporated companies before your competitors even hear of them." },
  { t: "Chartered Accountants & Tax Consultants", d: "Offer ROC compliance, statutory audit, and annual filing services to newly incorporated companies." },
  { t: "Banks, NBFCs & DSAs", d: "Cross-sell current accounts, corporate credit, POS terminals and working capital to new businesses." },
  { t: "SaaS & B2B Tools", d: "Target new companies with CRM, accounting, HRMS, payroll and productivity solutions from day one." },
  { t: "Legal & Compliance Firms", d: "Prospect for trademark, GST registration, secretarial and legal advisory work." },
  { t: "Insurance Providers", d: "Reach out with group health, D&O, liability and asset insurance for new entities." },
];

export default function McaDatabase() {
  const path = "/mca-database";
  const title = "Buy MCA Company Database India | Newly Incorporated Companies Daily | Registry Feed";
  const description = "Fresh MCA registered company database — newly incorporated Pvt Ltd, LLP, OPC companies with verified contacts. Pan-India, all ROC jurisdictions. Trial ₹3,000.";
  const keywords = "buy mca database, mca data provider india, newly incorporated companies list, mca master data, roc company data daily, new company registration data, mca database india";
  const bc = [{ name: "Home", path: "/" }, { name: "MCA Company Database", path }];
  const jsonLd = [
    ...pageSchema({ title, description, path, breadcrumbs: bc }),
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "MCA Company Database",
      description: "Daily-updated database of newly incorporated MCA companies and LLPs across India, sourced from official Registrar of Companies filings.",
      brand: { "@type": "Brand", name: "Registry Feed" },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: String(PRICING.mca.price),
        availability: "https://schema.org/InStock",
        url: "https://registryfeed.com/mca-database",
      },
    },
  ];
  return (
    <PublicPageLayout breadcrumbs={bc}>
      <Seo title={title} description={description} keywords={keywords} path={path} jsonLd={jsonLd} />

      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> MCA Company Database
          </div>
          <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            MCA Company Database — Newly Incorporated Companies, Daily Updated
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Registry Feed compiles a fresh MCA company database every day from official Registrar of Companies (ROC) filings. Reach directors of newly incorporated Pvt Ltd, LLP, OPC and Public companies across all 36 states and union territories.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.mca} label="Get MCA Data on WhatsApp" testid="mca-cta-whatsapp" />
            <Link to="/sample-data"><Button size="lg" variant="outline">Request Sample</Button></Link>
            <Link to="/pricing"><Button size="lg" variant="ghost">Get Pricing <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* What is MCA Database — keyword-rich explainer */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">What is MCA Database?</h2>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
            The MCA database is the official record of companies newly incorporated with the Ministry of Corporate Affairs — Private Limited companies, LLPs and One Person Companies (OPC). Registry Feed is a specialised MCA data provider India businesses rely on for a clean, daily updated newly incorporated companies list. Every morning we collect ROC company data daily from all ROC jurisdictions, clean it, and deliver it to your dashboard as ready-to-use Excel/CSV files. Each record is built from MCA master data — company name, CIN, director name, state, ROC, registration date and business type. When you buy MCA database access from Registry Feed, you reach companies in their very first days of operation — the moment they need bank accounts, accounting, compliance, software, trademarks, insurance and office setup. Fresh new company registration data means you are the first to call, not the tenth.
          </p>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Available data fields</h2>
          <p className="mt-2 text-muted-foreground text-sm">Fields available in the MCA company database (subject to what is publicly filed and available in ROC records).</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {DATA_FIELDS.map((f) => (
              <div key={f} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
                <Database className="h-4 w-4 text-primary" strokeWidth={2} /> {f}
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Registry Feed does not guarantee any specific data field is populated for every row — availability depends on what the entity has filed with the Registrar.</p>
        </div>
      </section>

      {/* Who uses MCA data — updated per prompt */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Who Uses MCA Data?</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "CA Firms", d: "Incorporation follow-ups, GST registration, accounting & audit services." },
              { t: "Banks", d: "Current accounts, business loans, merchant services for newly incorporated companies." },
              { t: "Software Companies", d: "ERP, accounting, CRM and HR software sales to fresh businesses." },
              { t: "Trademark Agents", d: "Trademark and brand registration services for new companies." },
              { t: "Consultants", d: "Compliance, legal, funding and business advisory for new entities." },
              { t: "B2B Sales Teams", d: "Any B2B product or service sold to newly incorporated companies." },
            ].map((u) => (
              <div key={u.t} className="rounded-lg border border-border bg-card p-5">
                <div className="font-heading font-semibold">{u.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* State-wise MCA Coverage */}
      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">State-wise MCA Coverage</h2>
          <p className="mt-3 text-sm text-muted-foreground">Newly incorporated companies from all 36 states and union territories, including:</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
            {["Maharashtra","Delhi","Gujarat","Karnataka","Tamil Nadu","Uttar Pradesh","West Bengal","Telangana","Rajasthan","Haryana"].map((s) => (
              <div key={s} className="rounded-md border border-border bg-card px-3 py-2 text-center">{s}</div>
            ))}
          </div>
          <div className="mt-4 text-sm"><Link to="/data-coverage" className="text-primary hover:underline">View full pan-India coverage →</Link></div>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Coverage &amp; delivery</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
            {COVERAGE_FACTS.map((c) => (
              <li key={c} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {c}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.mca} label="Buy MCA Database" testid="mca-cta-buy" />
            <Link to="/pricing"><Button variant="outline">See Pricing</Button></Link>
            <Link to="/contact"><Button variant="ghost">Talk to Sales</Button></Link>
          </div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
